import { useOrganization, SignedIn } from "@clerk/clerk-react";
import { usePlans } from "@clerk/clerk-react/experimental";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { apiFetch } from "@/lib/api";
import { Spinner } from "@/ui/spinner";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { IconCheck, IconX, IconSparkles, IconPlus } from "@tabler/icons-react";
import { cn } from "@/lib/utils";

// Which plan to highlight as "Most Popular"
const POPULAR_PLAN_ID = "orgpro";

// Get all unique features across plans for the comparison table
function getAllFeatures(
  plans: Array<{ features?: Array<{ id: string; name: string }> }>
) {
  const featureMap = new Map<string, string>();
  plans.forEach((plan) => {
    plan.features?.forEach((f) => {
      if (!featureMap.has(f.id)) {
        featureMap.set(f.id, f.name);
      }
    });
  });
  return Array.from(featureMap.entries()).map(([id, name]) => ({ id, name }));
}

export function OnboardingPricingPage() {
  const navigate = useNavigate();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { data: plans, isLoading: plansLoading } = usePlans({
    for: "organization",
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!orgLoaded || !organization || hasSynced.current) return;

    const syncOrg = async () => {
      hasSynced.current = true;
      setIsSyncing(true);
      try {
        await apiFetch<{ organization: { id: string } }>(
          "/organizations/sync",
          {
            method: "POST",
            body: JSON.stringify({
              clerkOrgId: organization.id,
              name: organization.name,
              slug: organization.slug || organization.id,
              logoUrl: organization.imageUrl,
            }),
          }
        );
      } catch (error) {
        console.error("Failed to sync organization:", error);
      } finally {
        setIsSyncing(false);
      }
    };

    syncOrg();
  }, [orgLoaded, organization]);

  if (!orgLoaded || isSyncing || plansLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4 text-center">
          <Spinner className="size-6 text-primary" />
          <p className="text-sm text-zinc-500">Setting things up...</p>
        </div>
      </div>
    );
  }

  const displayPlans = plans || [];
  const allFeatures = getAllFeatures(displayPlans);

  // Check if a plan has a specific feature
  const planHasFeature = (
    plan: (typeof displayPlans)[0],
    featureId: string
  ) => {
    return plan.features?.some((f) => f.id === featureId);
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4 py-12 bg-grid-pattern">
      <div className="fixed inset-0 bg-gradient-radial pointer-events-none -z-10" />

      <div className="relative w-full max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2.5 mb-4">
            <img
              src="/loomii-pink-white.png"
              alt="Loomii"
              className="w-10 h-10"
            />
          </div>
          <h1 className="text-3xl font-semibold text-zinc-100 mb-3">
            Choose your plan
          </h1>
          <p className="text-sm text-zinc-400">
            5-day free trial included · Cancel anytime
          </p>
        </div>

        {/* Pricing Table */}
        <div className="relative">
          {/* Corner + decorations */}
          <div className="absolute -top-3 -left-3 text-primary">
            <IconPlus className="w-6 h-6" strokeWidth={1} />
          </div>
          <div className="absolute -top-3 -right-3 text-primary">
            <IconPlus className="w-6 h-6" strokeWidth={1} />
          </div>
          <div className="absolute -bottom-3 -left-3 text-primary">
            <IconPlus className="w-6 h-6" strokeWidth={1} />
          </div>
          <div className="absolute -bottom-3 -right-3 text-primary">
            <IconPlus className="w-6 h-6" strokeWidth={1} />
          </div>

          <div className="bg-zinc-900/60 border border-zinc-800 overflow-hidden backdrop-blur-sm">
            {/* Plan Headers */}
            <div
              className={cn(
                "grid border-b border-zinc-800",
                `grid-cols-[1fr_repeat(${displayPlans.length},minmax(180px,1fr))]`
              )}
              style={{
                gridTemplateColumns: `1fr repeat(${displayPlans.length}, minmax(180px, 1fr))`,
              }}
            >
              {/* Empty cell for feature column */}
              <div className="p-5 border-r border-zinc-800/50">
                <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">
                  Compare plans
                </p>
              </div>

              {/* Plan columns */}
              {displayPlans.map((plan) => {
                const isPopular = plan.id === POPULAR_PLAN_ID;
                return (
                  <div
                    key={plan.id}
                    className={cn(
                      "p-5 border-r border-zinc-800/50 last:border-r-0 flex flex-col items-center text-center",
                      isPopular && "bg-primary/5"
                    )}
                  >
                    {isPopular && (
                      <Badge className="bg-primary/20 text-primary border-0 text-[10px] px-2 py-0.5 mb-2">
                        <IconSparkles className="w-3 h-3 mr-1" />
                        Popular
                      </Badge>
                    )}
                    <h3 className="text-base font-semibold text-zinc-100 self-start">
                      {plan.name}
                    </h3>
                    {plan.description && (
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-2 self-start text-left">
                        {plan.description}
                      </p>
                    )}

                    {/* Price */}
                    <div className="mt-3 mb-4 self-start">
                      <span
                        className={cn(
                          "text-2xl font-bold",
                          isPopular ? "text-primary" : "text-zinc-100"
                        )}
                      >
                        {plan.fee.currencySymbol}
                        {plan.fee.amountFormatted}
                      </span>
                      <span className="text-zinc-500 text-xs ml-1">/mo</span>
                    </div>

                    {/* CTA Button */}
                    <SignedIn>
                      <Button
                        size="sm"
                        className="w-full h-9 text-sm font-medium bg-primary hover:bg-primary/90 text-white"
                        onClick={() =>
                          navigate({
                            to: "/onboarding/checkout",
                            search: { planId: plan.id, planPeriod: "month" },
                          })
                        }
                      >
                        Get started
                      </Button>
                    </SignedIn>
                  </div>
                );
              })}
            </div>

            {/* Feature Rows */}
            {allFeatures.map((feature, idx) => (
              <div
                key={feature.id}
                className={cn(
                  "grid border-b border-zinc-800/50 last:border-b-0",
                  idx % 2 === 0 && "bg-zinc-900/30"
                )}
                style={{
                  gridTemplateColumns: `1fr repeat(${displayPlans.length}, minmax(180px, 1fr))`,
                }}
              >
                {/* Feature name */}
                <div className="px-5 py-3 border-r border-zinc-800/50 flex items-center">
                  <span className="text-sm text-zinc-400">{feature.name}</span>
                </div>

                {/* Plan feature values */}
                {displayPlans.map((plan) => {
                  const hasFeature = planHasFeature(plan, feature.id);
                  const isPopular = plan.id === POPULAR_PLAN_ID;

                  return (
                    <div
                      key={plan.id}
                      className={cn(
                        "px-5 py-3 border-r border-zinc-800/50 last:border-r-0 flex items-center justify-center",
                        isPopular && "bg-primary/5"
                      )}
                    >
                      {hasFeature ? (
                        <IconCheck className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <IconX className="w-4 h-4 text-zinc-700" />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-zinc-500 mt-8">
          All plans include a{" "}
          <span className="text-primary">5-day free trial</span>.
        </p>
      </div>
    </div>
  );
}
